import React, { useState, useEffect } from 'react';
import { losApi } from '../../losApi';
import { toast } from '../../../hooks/use-toast';
import { fmt$, ModuleHeader, TabBtn, NumField, SelectFld } from '../shared/formFields';
import AddressFields from '../../shared/AddressFields';
import PickSelect from '../../shared/PickSelect';
import { LOAN_PURPOSE_LABELS, LOAN_PROGRAM_LABELS, PROPERTY_TYPE_LABELS, optionsFrom } from '../../shared/labels';
import { ltvStack } from '../shared/ltv';
import { proposedPayment } from '../shared/proposedPayment';
import AriveBadge from '../../board/AriveBadge';
import { ariveDeepLink, isAriveManaged, toastAriveConflict, AriveLocked } from '../../ariveManaged';

// Loan FICO is the loan-level score (`estimated_credit_score`), seeded from the borrower's.
const formFrom = (loan) => ({
  purchase_price: loan.purchase_price,
  appraised_value: loan.appraised_value,
  loan_amount: loan.loan_amount,
  note_rate: loan.note_rate,
  qualifying_rate: loan.qualifying_rate,
  term_months: loan.term_months,
  property_type: loan.property_type,
  occupancy: loan.occupancy,
  loan_program: loan.loan_program,
  loan_purpose: loan.loan_purpose,
  lien_position: loan.lien_position || 'first',
  amortization_type: loan.amortization_type || 'fixed',
  interest_only: !!loan.interest_only,
  impound_waiver: !!loan.impound_waiver,
  subordinate_financing: loan.subordinate_financing,
  heloc_credit_limit: loan.heloc_credit_limit,
  estimated_credit_score: loan.estimated_credit_score ?? loan.urla?.borrower?.estimated_credit_score,
  property_address: loan.property_address || {},
  purchase_credits: loan.purchase_credits || [],
});

// The tabs group the LoanPurpose values; the select beside them picks the exact one.
const PURPOSE_GROUPS = [
  { key: 'purchase', label: 'Purchase', purposes: ['purchase'] },
  { key: 'refinance', label: 'Refinance', purposes: ['rate_term_refi', 'cash_out_refi'] },
  { key: 'other', label: 'Other', purposes: ['construction', 'heloc', 'other'] },
];
const purposeGroup = (purpose) => (PURPOSE_GROUPS.find((g) => g.purposes.includes(purpose)) || PURPOSE_GROUPS[0]).key;

// A cleared optional field saves as null, not 0.
const numOrNull = (v) => (v === '' || v == null ? null : Number(v));

export function LoanPropertyModule({ loan, onChange }) {
  const [saving, setSaving] = useState(false);
  // Amount, rate, term, price, purpose and address are Arive-owned on a synced loan.
  const locked = isAriveManaged(loan);
  const [form, setForm] = useState(() => formFrom(loan));
  useEffect(() => { setForm(formFrom(loan)); }, [loan]);
  const upd = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const down = Math.max(0, (Number(form.purchase_price) || 0) - (Number(form.loan_amount) || 0));
  const { ltv, cltv, hcltv } = ltvStack(form);

  const save = async () => {
    setSaving(true);
    try {
      await losApi.updateLoan(loan.loan_file_id, form);
      toast({ title: 'Loan & property saved' });
      onChange();
    } catch (e) {
      if (!toastAriveConflict(e)) toast({ title: 'Failed', description: e.response?.data?.detail || 'Error' });
    } finally { setSaving(false); }
  };

  return (
    <div className="space-y-4">
      <ModuleHeader title="Loan & Property" onSave={save} saving={saving} />
      {locked && (
        <div className="flex items-center gap-2 text-[12px] text-slate-500" data-testid="lp-arive-note">
          <AriveBadge deepLink={ariveDeepLink(loan)} /> Loan terms, the property and purchase credits are edited in Arive. Loan FICO stays editable here.
        </div>
      )}

      <div className="grid lg:grid-cols-3 gap-4">
        <div className="lg:col-span-2 space-y-4">
          {/* Purpose: Purchase / Refinance / Other, then the exact purpose for the last two */}
          <div className="bg-white rounded-xl border border-slate-200 p-5">
            <div className="flex items-center gap-1 mb-4 border-b border-slate-100 pb-2">
              {PURPOSE_GROUPS.map((g) => (
                <TabBtn key={g.key} active={purposeGroup(form.loan_purpose) === g.key} onClick={() => !locked && upd('loan_purpose', g.purposes[0])}>{g.label}</TabBtn>
              ))}
              {purposeGroup(form.loan_purpose) !== 'purchase' && (
                <div className="ml-auto w-56" data-testid="lp-purpose">
                  <SelectFld label="Purpose" value={form.loan_purpose} onChange={(v) => !locked && upd('loan_purpose', v)}
                    options={PURPOSE_GROUPS.find((g) => g.key === purposeGroup(form.loan_purpose)).purposes.map((k) => [k, LOAN_PURPOSE_LABELS[k]])} />
                </div>
              )}
            </div>

            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
              <NumField label="Purchase Price" money readOnly={locked} value={form.purchase_price} onChange={(v) => upd('purchase_price', Number(v))} testId="lp-purchase-price" />
              <NumField label="Appraised Value" money readOnly={locked} value={form.appraised_value} onChange={(v) => upd('appraised_value', Number(v))} testId="lp-appraised" />
              <NumField label="Down Payment" money value={down} readOnly testId="lp-down" />
              <NumField label="Base Loan Amt" money readOnly={locked} value={form.loan_amount} onChange={(v) => upd('loan_amount', Number(v))} testId="lp-loan-amount" />
            </div>

            <div className="grid grid-cols-2 md:grid-cols-3 gap-3 mb-4">
              <AriveLocked loan={loan}><SelectFld label="Mortgage Type" value={form.loan_program} onChange={(v) => upd('loan_program', v)}
                options={optionsFrom(LOAN_PROGRAM_LABELS)} />
              <AriveLocked loan={loan}><SelectFld label="Lien Position" value={form.lien_position} onChange={(v) => upd('lien_position', v)}
                options={[['first', 'First'], ['second', 'Second'], ['third', 'Third']]} /></AriveLocked></AriveLocked>
              <AriveLocked loan={loan}><SelectFld label="Amortization Type" value={form.amortization_type} onChange={(v) => upd('amortization_type', v)}
                options={[['fixed', 'Fixed'], ['arm', 'ARM'], ['step', 'Step Rate'], ['gpm', 'GPM']]} /></AriveLocked>
            </div>

            {/* LTV bars */}
            <div className="grid grid-cols-3 gap-3 mb-4">
              <LtvBar label="LTV" value={ltv} max={form.loan_program === 'fha' ? 96.5 : 80} />
              <LtvBar label="CLTV" value={cltv} max={95} />
              <LtvBar label="HCLTV" value={hcltv} max={95} />
            </div>
            <div className="grid grid-cols-2 gap-3 mb-4">
              <NumField label="Subordinate Financing (closed-end)" money readOnly={locked} value={form.subordinate_financing} onChange={(v) => upd('subordinate_financing', numOrNull(v))} testId="lp-sub-financing" />
              <NumField label="HELOC Credit Limit" money readOnly={locked} value={form.heloc_credit_limit} onChange={(v) => upd('heloc_credit_limit', numOrNull(v))} testId="lp-heloc-limit" />
            </div>

            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              <NumField label="Note Rate %" readOnly={locked} value={form.note_rate} onChange={(v) => upd('note_rate', Number(v))} testId="lp-note-rate" />
              <NumField label="Qualifying Rate %" readOnly={locked} value={form.qualifying_rate} onChange={(v) => upd('qualifying_rate', numOrNull(v))} testId="lp-qual-rate" />
              <NumField label="Amortization Term (mo)" readOnly={locked} value={form.term_months} onChange={(v) => upd('term_months', Number(v))} testId="lp-term" />
              <NumField label="Loan FICO" value={form.estimated_credit_score} onChange={(v) => upd('estimated_credit_score', numOrNull(v))} testId="lp-fico" />
            </div>

            <div className="flex items-center gap-4 mt-4 pt-3 border-t border-slate-100">
              <label className="flex items-center gap-2 text-[13px]">
                <input type="checkbox" disabled={locked} checked={form.interest_only} onChange={(e) => upd('interest_only', e.target.checked)} data-testid="lp-interest-only" />
                Interest Only
              </label>
              <label className="flex items-center gap-2 text-[13px]">
                <input type="checkbox" disabled={locked} checked={form.impound_waiver} onChange={(e) => upd('impound_waiver', e.target.checked)} data-testid="lp-impound-waiver" />
                Impound Waiver
              </label>
            </div>
          </div>

          {/* Property Details */}
          <div className="bg-white rounded-xl border border-slate-200 p-5">
            <div className="text-[13px] font-semibold mb-3" style={{ color: '#030524' }}>Subject Property</div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              <fieldset disabled={locked} style={{ display: 'contents' }}>
                <AddressFields value={form.property_address} onChange={(a) => setForm(f => ({ ...f, property_address: a }))} testIdPrefix="lp" label="" />
              </fieldset>
              <AriveLocked loan={loan}><SelectFld label="Property Type" value={form.property_type} onChange={(v) => upd('property_type', v)}
                options={optionsFrom(PROPERTY_TYPE_LABELS)} />
              <AriveLocked loan={loan}><SelectFld label="Occupancy" value={form.occupancy} onChange={(v) => upd('occupancy', v)}
                options={[['primary', 'Primary'], ['second_home', 'Second Home'], ['investment', 'Investment']]} /></AriveLocked></AriveLocked>
            </div>
          </div>
        </div>

        {/* Right rail — Proposed Monthly Payment */}
        <div className="space-y-4">
          <ProposedPaymentCard loan={loan} form={form} />
          <AriveLocked loan={loan}><PurchaseCreditsCard credits={form.purchase_credits} onChange={(c) => upd('purchase_credits', c)} /></AriveLocked>
        </div>
      </div>
    </div>
  );
}

function LtvBar({ label, value, max }) {
  const pct = Math.min(100, ((value || 0) / max) * 100);
  const overMax = value != null && value > max;
  return (
    <div>
      <div className="flex items-baseline justify-between mb-1">
        <span className="text-[10px] uppercase tracking-widest font-bold text-slate-500">{label}</span>
        <span className={`text-[12px] font-bold ${overMax ? 'text-red-600' : ''}`} style={overMax ? {} : { color: '#030524' }}>{value == null ? '—' : `${value}%`}</span>
      </div>
      <div className="h-1.5 bg-slate-100 rounded-full overflow-hidden">
        <div className="h-full rounded-full" style={{ width: `${pct}%`, background: overMax ? '#dc2626' : '#6BA82E' }} />
      </div>
      <div className="text-[10px] text-slate-400 mt-0.5">Max {max}%</div>
    </div>
  );
}

function ProposedPaymentCard({ loan, form }) {
  const { firstMortgage, other: otherFin, hoi, taxes, mi, total } = proposedPayment({
    loan_amount: form.loan_amount, note_rate: form.note_rate, term_months: form.term_months, le_fees: loan.le_fees,
    other_new_mortgages: loan.urla?.other_new_mortgages,
  });
  const hoa = 0, other = 0;

  return (
    <div className="bg-white rounded-xl border border-slate-200 p-4" data-testid="lp-payment-card">
      <div className="flex items-baseline justify-between mb-3">
        <div className="text-[13px] font-semibold" style={{ color: '#030524' }}>Proposed Monthly Payment</div>
        <span className="text-[9px] uppercase font-bold text-slate-400">Estimate</span>
      </div>
      <div className="space-y-1.5 text-[12px]">
        <PayRow label="First Mortgage" value={firstMortgage} isCalc />
        <PayRow label="Other Financing" value={otherFin} isCalc={otherFin > 0} />
        <PayRow label="Homeowners Insurance" value={hoi} isCalc={hoi > 0} />
        <PayRow label="Supplemental" value={0} />
        <PayRow label="Property Taxes" value={taxes} isCalc={taxes > 0} />
        <PayRow label="Mortgage Insurance" value={mi} isCalc={mi > 0} />
        <PayRow label="Association Dues" value={hoa} />
        <PayRow label="Other" value={other} />
        <div className="pt-2 mt-2 border-t border-slate-100 flex justify-between items-center">
          <span className="text-[11px] uppercase tracking-widest font-bold text-slate-500">Total PITI</span>
          <span className="font-bold text-[15px]" style={{ color: '#6BA82E' }} data-testid="lp-payment-total">{fmt$(total)}</span>
        </div>
        {(hoi === 0 || taxes === 0) && (
          <div className="text-[10px] text-slate-400 italic" data-testid="lp-payment-note">
            Insurance, taxes and MI come from the LE fees tab; insurance or taxes not entered yet.
          </div>
        )}
      </div>
    </div>
  );
}

function PayRow({ label, value, isCalc }) {
  return (
    <div className="flex justify-between items-center">
      <span className="text-slate-600">{label}</span>
      <div className="flex items-center gap-2">
        {isCalc && <span className="text-[9px] uppercase font-bold text-slate-400">calc</span>}
        <span className="font-semibold" style={{ color: '#030524' }}>{fmt$(value)}</span>
      </div>
    </div>
  );
}

// URLA section 4b credit types; keys match the backend PurchaseCreditType.
const CREDIT_TYPES = [
  ['earnest_money', 'Earnest money'],
  ['employer_assisted_housing', 'Employer assisted housing'],
  ['lender_credit', 'Lender credit'],
  ['related_party_contribution', 'Related party contribution'],
  ['sweat_equity', 'Sweat equity'],
  ['seller_credit', 'Seller credit'],
  ['trade_equity', 'Trade equity'],
  ['other', 'Other'],
];
const creditLabel = (k) => (CREDIT_TYPES.find(([key]) => key === k) || [k, k])[1];

function PurchaseCreditsCard({ credits, onChange }) {
  const [adding, setAdding] = useState(false);
  const [type, setType] = useState('seller_credit');
  const [amount, setAmount] = useState('');
  const total = credits.reduce((sum, c) => sum + (Number(c.amount) || 0), 0);
  const valid = Number(amount) > 0;
  const add = () => {
    if (!valid) return;
    onChange([...credits, { credit_type: type, amount: Number(amount) }]);
    setAdding(false); setAmount(''); setType('seller_credit');
  };
  return (
    <div className="bg-white rounded-xl border border-slate-200 p-4" data-testid="lp-credits-card">
      <div className="flex items-center justify-between mb-2">
        <div className="text-[13px] font-semibold" style={{ color: '#030524' }}>Purchase Credits</div>
        {!adding && (
          <button type="button" className="text-[11px] font-semibold hover:underline" style={{ color: '#070B5F' }}
            onClick={() => setAdding(true)} data-testid="lp-credit-add">+ Add</button>
        )}
      </div>
      {credits.length === 0 && !adding && <div className="text-[11px] text-slate-400 italic">No credits added.</div>}
      <div className="space-y-1.5 text-[12px]">
        {credits.map((c, i) => (
          <div key={`${c.credit_type}-${i}`} className="flex justify-between items-center" data-testid={`lp-credit-${i}`}>
            <span className="text-slate-600">{creditLabel(c.credit_type)}</span>
            <div className="flex items-center gap-2">
              <span className="font-semibold" style={{ color: '#030524' }}>{fmt$(c.amount)}</span>
              <button type="button" className="text-[11px] text-slate-400 hover:text-red-600" aria-label={`Remove ${creditLabel(c.credit_type)}`}
                onClick={() => onChange(credits.filter((_, j) => j !== i))} data-testid={`lp-credit-remove-${i}`}>✕</button>
            </div>
          </div>
        ))}
        {credits.length > 0 && (
          <div className="pt-2 mt-2 border-t border-slate-100 flex justify-between items-center">
            <span className="text-[11px] uppercase tracking-widest font-bold text-slate-500">Total credits</span>
            <span className="font-bold" style={{ color: '#6BA82E' }} data-testid="lp-credit-total">{fmt$(total)}</span>
          </div>
        )}
      </div>
      {adding && (
        <div className="mt-2 space-y-2" data-testid="lp-credit-form">
          <PickSelect
            label="Credit type"
            labelClassName="sr-only"
            value={type}
            onChange={setType}
            options={CREDIT_TYPES.map(([k, l]) => ({ value: k, label: l }))}
            testId="lp-credit-type"
          />
          <NumField label="Amount" money value={amount} onChange={setAmount} testId="lp-credit-amount" />
          <div className="flex gap-2">
            <button type="button" disabled={!valid} onClick={add} data-testid="lp-credit-save"
              className="text-[11px] font-bold px-3 py-1.5 rounded-lg text-white disabled:opacity-40" style={{ background: '#070B5F' }}>Add credit</button>
            <button type="button" onClick={() => { setAdding(false); setAmount(''); }} data-testid="lp-credit-cancel"
              className="text-[11px] font-semibold px-3 py-1.5 text-slate-500">Cancel</button>
          </div>
        </div>
      )}
    </div>
  );
}
